/**
 * Phase 129 (Plan 129-10, D-15 / D-17) — the release-flag table, shared by the admin Release Flags
 * page and the project Engineering tab's Release flags card.
 *
 * Extracted from `pages/admin/ReleaseFlagsPage.tsx` (Phase 120): the rows, the three actions with
 * their busy state, both confirm modals and the toast. The caller supplies the rows and the three
 * actions as promise-returning functions (the admin hooks on the admin page, the project-scoped
 * hooks on the card), plus an optional sort. With no `sort` / `onSort` the headers are plain.
 *
 * There is deliberately no plain "turn on": the only way on is "Switch on (mark verified)"
 * (D-120-24), which verifies the whole ticket (one-way), clears its overrides and marks the
 * ticket Done.
 *
 * Security (T-120-07 / T-120-09): registry descriptions and task names render as React text nodes
 * (auto-escaped), never as HTML. Authorization is the server's job on every route.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppToast } from '../app-access/AppToast';
import type { AppToastState } from '../app-access/AppToast';
import { Modal } from '../common/Modal';
import { DATE_PLACEHOLDER } from '../../utils/dateFormat';
import { formatDisplayDate } from '../../utils/format';
import type {
  AdminReleaseFlagRow,
  AdminReleaseFlagTaskRef,
  ReleaseFlagSource,
  ReleaseFlagStaleTier,
  ReleaseFlagSwitchOnResult,
} from '../../services/api/releaseFlags';

const SOURCE_CLASSES: Record<ReleaseFlagSource, string> = {
  override: 'bg-amber-100 text-amber-800',
  verified: 'bg-green-100 text-green-800',
  default: 'bg-gray-100 text-gray-700',
};

const STALE_BADGES: Record<Exclude<ReleaseFlagStaleTier, 'ok'>, { label: string; classes: string }> = {
  warn: { label: 'getting old', classes: 'bg-amber-100 text-amber-800' },
  fail: { label: 'overdue for cleanup', classes: 'bg-red-100 text-red-800' },
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  // MKS-351 — dd.mm.yyyy from the shared util.
  const formatted = formatDisplayDate(iso);
  return formatted === DATE_PLACEHOLDER ? '—' : formatted;
}

/**
 * The " · <who>" tail of the override and Verified lines. The backend-resolved
 * "Lastname Firstname" is shown, and an id whose user row is gone reads
 * "Former user #19". An actor-less row (nothing recorded) contributes nothing.
 */
function actorSuffix(actor: { byUserId: number | null; byUserName: string | null }): string {
  if (actor.byUserId === null) return '';
  return ` · ${actor.byUserName ?? `Former user #${actor.byUserId}`}`;
}

/** One sentence telling the person what the switch-on actually did. */
export function describeSwitchOn(result: ReleaseFlagSwitchOnResult): string {
  const { ticket, reconcile } = result;
  switch (reconcile.status) {
    case 'reconciled':
      if (reconcile.taskDone === 'moved') return `${ticket} marked Done`;
      if (reconcile.taskDone === 'already_terminal') {
        return `${ticket} switched on; its task was already closed`;
      }
      return `${ticket} switched on; its task was not moved (${reconcile.taskDone ?? 'unknown'})`;
    case 'task_missing':
      return `${ticket} switched on; the ticket's task was not found here`;
    case 'task_ambiguous':
      return `${ticket} switched on; several tasks carry this code, so none was moved`;
    case 'flags_not_all_on':
      return `${ticket} verified; another flag of this ticket is still off, so its task stays open`;
    case 'staging_skipped':
      return `${ticket}: overrides cleared (a staging instance never verifies a ticket)`;
    case 'not_in_running_registry':
      return `${ticket} switched on; this build does not carry its flags`;
    case 'failed':
      return `${ticket} switched on, but marking its task Done failed — check the logs`;
    default:
      return `${ticket} switched on`;
  }
}

function TicketCell({ row }: { row: AdminReleaseFlagRow }) {
  if (row.task) {
    return (
      <Link
        to={`/projects/${row.task.projectId}/board?openTask=${row.task.id}`}
        className="text-blue-600 hover:underline"
        title={row.task.name}
      >
        {row.ticket}
      </Link>
    );
  }
  return <span>{row.ticket}</span>;
}

/**
 * MKS-273 — the ticket's creator, beside the ticket link. The name is already
 * resolved by the backend ("Lastname Firstname", the client's own name, or
 * `Robot` for a machine user); an unresolvable ticket reads "—".
 */
function CreatorCell({ row }: { row: AdminReleaseFlagRow }) {
  if (!row.creatorName) {
    return <span className="text-xs text-gray-400">—</span>;
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span>{row.creatorName}</span>
      {row.creatorKind === 'external_user' && (
        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
          client
        </span>
      )}
    </span>
  );
}

function TaskState({ task }: { task: AdminReleaseFlagTaskRef }) {
  return (
    <span className="text-xs text-gray-600">
      {task.code}
      {task.stateName ? ` · ${task.stateName}` : ''}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Sorting (MKS-273). The caller owns the state; the table only draws the headers.
// ---------------------------------------------------------------------------

export type SortKey = 'flag' | 'ticket' | 'creator' | 'added' | 'state' | 'verified';
export type SortDir = 'asc' | 'desc';
export interface SortState {
  key: SortKey;
  dir: SortDir;
}

function SortableHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState | null;
  onSort: (key: SortKey) => void;
}) {
  const active = sort && sort.key === sortKey ? sort : null;
  return (
    <th
      className="px-3 py-2 text-left text-xs font-semibold text-gray-700 cursor-pointer select-none hover:text-gray-900"
      aria-sort={active ? (active.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onClick={() => onSort(sortKey)}
    >
      {label}
      {active && <span className="ml-1">{active.dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

function PlainHeader({ label }: { label: string }) {
  return <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">{label}</th>;
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export interface ReleaseFlagTableActions {
  switchOn: (ticket: string) => Promise<ReleaseFlagSwitchOnResult>;
  switchOff: (key: string) => Promise<unknown>;
  reset: (key: string) => Promise<unknown>;
  switchOnPending: boolean;
  switchOffPending: boolean;
  resetPending: boolean;
}

export interface ReleaseFlagTableProps {
  rows: AdminReleaseFlagRow[];
  actions: ReleaseFlagTableActions;
  /** Current sort and its toggle. When `onSort` is absent the headers are plain. */
  sort?: SortState | null;
  onSort?: (key: SortKey) => void;
}

const SORTABLE_COLUMNS: ReadonlyArray<{ label: string; key: SortKey }> = [
  { label: 'Flag', key: 'flag' },
  { label: 'Ticket', key: 'ticket' },
  { label: 'Creator', key: 'creator' },
  { label: 'Added', key: 'added' },
  { label: 'State', key: 'state' },
  { label: 'Verified', key: 'verified' },
];

export function ReleaseFlagTable({ rows, actions, sort = null, onSort }: ReleaseFlagTableProps) {
  const [toast, setToast] = useState<AppToastState | null>(null);
  const [confirming, setConfirming] = useState<AdminReleaseFlagRow | null>(null);
  const [confirmingOn, setConfirmingOn] = useState<AdminReleaseFlagRow | null>(null);

  const busy = actions.switchOffPending || actions.resetPending || actions.switchOnPending;

  const handleSwitchOn = async (row: AdminReleaseFlagRow) => {
    try {
      const result = await actions.switchOn(row.ticket);
      setToast({ message: describeSwitchOn(result), variant: 'success' });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to switch the ticket on',
        variant: 'error',
      });
    } finally {
      setConfirmingOn(null);
    }
  };

  const handleSwitchOff = async (row: AdminReleaseFlagRow) => {
    try {
      await actions.switchOff(row.key);
      setToast({ message: `Switched off: ${row.key}`, variant: 'success' });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to switch the flag off',
        variant: 'error',
      });
    } finally {
      setConfirming(null);
    }
  };

  const handleReset = async (row: AdminReleaseFlagRow) => {
    try {
      await actions.reset(row.key);
      setToast({ message: `Override removed: ${row.key}`, variant: 'success' });
    } catch (err) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to reset the override',
        variant: 'error',
      });
    }
  };

  return (
    <>
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {SORTABLE_COLUMNS.map((column) =>
                onSort ? (
                  <SortableHeader
                    key={column.key}
                    label={column.label}
                    sortKey={column.key}
                    sort={sort}
                    onSort={onSort}
                  />
                ) : (
                  <PlainHeader key={column.key} label={column.label} />
                )
              )}
              <PlainHeader label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => {
              const alreadyOff = row.override !== null && row.override.enabled === false;
              const staleBadge = row.staleTier === 'ok' ? null : STALE_BADGES[row.staleTier];
              return (
                <tr key={row.key} data-testid={`flag-row-${row.key}`}>
                  <td className="px-3 py-2 text-sm">
                    <div className="font-mono font-medium">{row.key}</div>
                    <div className="text-xs text-gray-600">{row.description}</div>
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    <TicketCell row={row} />
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    <CreatorCell row={row} />
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    <div>{formatDate(row.addedAt)}</div>
                    <div className="text-xs text-gray-600">
                      {row.ageDays === null ? 'age unknown' : `${row.ageDays} d`}
                      {staleBadge && (
                        <span
                          className={`ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium ${staleBadge.classes}`}
                        >
                          {staleBadge.label}
                        </span>
                      )}
                      {row.ageDays === null && (
                        <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          invalid date
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span
                        className={`font-semibold ${row.effective ? 'text-green-700' : 'text-gray-700'}`}
                      >
                        {row.effective ? 'On' : 'Off'}
                      </span>
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${SOURCE_CLASSES[row.source]}`}
                      >
                        {row.source}
                      </span>
                    </div>
                    <div className="text-xs text-gray-600">
                      default {row.envDefault ? 'on' : 'off'} here
                    </div>
                    {row.override && (
                      <div className="text-xs text-gray-600">
                        override {row.override.enabled ? 'on' : 'off'} · {formatDate(row.override.at)}
                        {actorSuffix(row.override)}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm">
                    {row.verified ? (
                      <div className="space-y-0.5">
                        <div className="text-xs text-gray-700">
                          {formatDate(row.verified.at)}
                          {row.verified.source ? ` · ${row.verified.source}` : ''}
                          {actorSuffix(row.verified)}
                        </div>
                        {row.cleanupTask && (
                          <div>
                            <span className="text-xs text-gray-500">Cleanup: </span>
                            <TaskState task={row.cleanupTask} />
                            {row.cleanupTask.isTerminal && (
                              <span className="ml-2 inline-block px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                cleanup closed — needs a human
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-sm whitespace-nowrap">
                    {row.source !== 'verified' && (
                      <button
                        type="button"
                        onClick={() => setConfirmingOn(row)}
                        disabled={busy}
                        className="text-sm px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 mr-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Mark the ticket verified: turns its flags on for everyone and closes the ticket"
                      >
                        Switch on (mark verified)
                      </button>
                    )}
                    {!alreadyOff && (
                      <button
                        type="button"
                        onClick={() => setConfirming(row)}
                        disabled={busy}
                        className="text-sm px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Force this flag off everywhere until the override is reset"
                      >
                        Switch off
                      </button>
                    )}
                    {row.override && (
                      <button
                        type="button"
                        onClick={() => handleReset(row)}
                        disabled={busy}
                        className="text-sm px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 ml-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Remove the override; the flag follows verification or the environment default"
                      >
                        Reset override
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AppToast toast={toast} onDismiss={() => setToast(null)} />

      {confirmingOn && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmingOn(null)}
          title={`Switch on ${confirmingOn.ticket}?`}
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmingOn(null)}
                disabled={actions.switchOnPending}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSwitchOn(confirmingOn)}
                disabled={actions.switchOnPending}
                className="text-sm px-3 py-1.5 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
              >
                {actions.switchOnPending ? 'Switching on…' : 'Confirm switch on'}
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-700">
            This turns the feature behind ticket <code>{confirmingOn.ticket}</code> on for everyone
            here, marks the ticket verified (this cannot be undone) and moves its task to Done.
            The cleanup task that removes the flag from the code is filed in a
            nightly batch 48 hours later; switching the flag off before then keeps it out. Any
            switch-off override on its flags is removed.
          </p>
        </Modal>
      )}

      {confirming && (
        <Modal
          isOpen={true}
          onClose={() => setConfirming(null)}
          title={`Switch off ${confirming.key}?`}
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(null)}
                disabled={actions.switchOffPending}
                className="text-sm px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSwitchOff(confirming)}
                disabled={actions.switchOffPending}
                className="text-sm px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {actions.switchOffPending ? 'Switching off…' : 'Confirm switch off'}
              </button>
            </div>
          }
        >
          <p className="text-sm text-gray-700">
            The feature behind <code>{confirming.key}</code> (ticket {confirming.ticket}) goes dark
            for everyone on the next request, even if the ticket is verified. Use “Reset override”
            to hand control back to verification.
          </p>
        </Modal>
      )}
    </>
  );
}
